import { motion } from 'framer-motion';
import {
  Send,
  CheckCircle,
  AlertCircle,
  Upload,
  X,
  Paperclip,
  Sun,
  Moon,
  MapPin,
  Mail,
  ExternalLink,
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';
import { logEvent } from '../services/loggingService';
import { SEO } from '../shared/components/SEO';
import { GGJLogoFull } from '../components/Brand/GGJLogo';
import { getLocalizedContactSeo } from '../content/i18n/marketingPageSeo';

/** Public HQ — Electronic City Phase 1 (no flat/building name). */
const HQ_ADDRESS_QUERY = 'Electronic City Phase 1, Bengaluru, Karnataka 560100';
const HQ_MAP_EMBED_SRC = `https://maps.google.com/maps?q=${encodeURIComponent(HQ_ADDRESS_QUERY)}&z=15&output=embed`;
const HQ_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(HQ_ADDRESS_QUERY)}`;
const HQ_SUPPORT_EMAIL = 'support@gograbjob.com';

interface ContactUsPageProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'privacy-policy' | 'cookie-policy' | 'terms-of-service' | 'gdpr-compliance' | 'refund-cancellation' | 'faqs' | 'about-us' | 'blog' | 'contact-us') => void;
}

const CONTACT_ILLUSTRATIONS = {
  light: '/images/contact-support-illustration-light.png',
  dark: '/images/contact-support-illustration-dark.png',
} as const;

function ContactIllustration({ isDark, alt }: { isDark: boolean; alt: string }) {
  const src = isDark ? CONTACT_ILLUSTRATIONS.dark : CONTACT_ILLUSTRATIONS.light;

  return (
    <div className="contact-page__illus-wrap">
      <img
        key={src}
        src={src}
        alt={alt}
        loading="lazy"
        draggable={false}
        className="contact-page__illus-img"
      />
    </div>
  );
}

function ContactHeadquarters({ mapTitle }: { mapTitle: string }) {
  const { t } = useTranslation();

  return (
    <section className="contact-page__hq" aria-labelledby="contact-hq-heading">
      <div className="contact-page__hq-info">
        <div className="contact-page__hq-heading-row">
          <MapPin className="contact-page__hq-icon" aria-hidden strokeWidth={1.75} />
          <h3 id="contact-hq-heading" className="contact-page__hq-heading">
            {t('views.contact.headquarters')}
          </h3>
        </div>
        <p className="contact-page__hq-company">{t('views.contact.companyName')}</p>
        <address className="contact-page__hq-address">
          {t('views.contact.addressLine1')}
          <br />
          {t('views.contact.addressLine2')}
        </address>
        <a
          href={`mailto:${HQ_SUPPORT_EMAIL}`}
          className="contact-page__hq-link"
        >
          <Mail className="h-4 w-4 shrink-0" aria-hidden strokeWidth={1.75} />
          {t('views.contact.supportEmail')}
        </a>
        <a
          href={HQ_DIRECTIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="contact-page__hq-link contact-page__hq-link--directions"
        >
          {t('views.contact.getDirections')}
          <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden strokeWidth={1.75} />
        </a>
      </div>
      <div className="contact-page__hq-map">
        <iframe
          title={mapTitle}
          src={HQ_MAP_EMBED_SRC}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
    </section>
  );
}

const ContactUsPage: React.FC<ContactUsPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const contactSeo = getLocalizedContactSeo(t);
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'validation-error'>('idle');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setSubmitStatus('idle');
    if (validationErrors[e.target.name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const newFilesArray = Array.from(files);
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = newFilesArray.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setValidationErrors(prev => ({ ...prev, files: t('views.contact.fileTypeError', { name: file.name }) }));
        return false;
      }
      if (file.size > maxSize) {
        setValidationErrors(prev => ({ ...prev, files: t('views.contact.fileSizeError', { name: file.name }) }));
        return false;
      }
      return true;
    });
    
    if (validFiles.length === files.length) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.files;
        return newErrors;
      });
    }
    
    const newFiles = Array.from(files).slice(0, 10 - attachedFiles.length);
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = t('views.contact.firstNameRequired');
    }
    
    if (!formData.email.trim()) {
      errors.email = t('views.contact.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('views.contact.emailInvalid');
    }
    
    if (!formData.message.trim()) {
      errors.message = t('views.contact.messageRequired');
    } else if (formData.message.trim().length < 10) {
      errors.message = t('views.contact.messageMinLength');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('validation-error');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const attachmentUrls: string[] = [];
      
      for (const file of attachedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `contact-attachments/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('contact-form-attachments')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw new Error(`Failed to upload file ${file.name}: ${uploadError.message}`);
        }
        
        const { data: { publicUrl } } = supabase.storage
          .from('contact-form-attachments')
          .getPublicUrl(filePath);
        
        attachmentUrls.push(publicUrl);
      }

      await supabase.auth.getSession();
      
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-contact-email`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY as string}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          message: formData.message,
          attachmentUrls: attachmentUrls,
          turnstileToken: null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || `Failed to send message: ${response.statusText}`);
      }

      const responseData = await response.json();
      
      setSubmitStatus('success');
      
      await logEvent({
        level: 'info',
        action: 'contact_form_submitted',
        details: {
          hasName: !!formData.firstName,
          hasEmail: !!formData.email,
          attachedFiles: attachedFiles.length,
          method: 'edge_function',
          emailId: responseData.emailId
        }
      });
      
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: ''
        });
        setAttachedFiles([]);
        setSubmitStatus('idle');
      }, 3000);
      
    } catch (error: unknown) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    onNavigate?.('home');
  };

  const themeToggle = (
    <button
      type="button"
      onClick={toggleTheme}
      className="contact-page__theme-btn"
      aria-label={isDark ? t('layout.ariaSwitchToLight') : t('layout.ariaSwitchToDark')}
    >
      {isDark ? <Sun className="h-5 w-5" strokeWidth={1.75} /> : <Moon className="h-5 w-5" strokeWidth={1.75} />}
    </button>
  );

  return (
    <div className="contact-page relative">
      <SEO
        title={contactSeo.title}
        description={contactSeo.description}
        canonical="https://www.gograbjob.com/contact-us"
        includeGeo
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'GoGrabJob',
          legalName: 'Zykes Technologies LLP',
          url: 'https://www.gograbjob.com/contact-us',
          email: HQ_SUPPORT_EMAIL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Electronic City Phase 1',
            addressLocality: 'Bengaluru',
            addressRegion: 'KA',
            postalCode: '560100',
            addressCountry: 'IN',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 12.8431,
            longitude: 77.6565,
          },
          parentOrganization: {
            '@id': 'https://www.gograbjob.com/#organization',
          },
        }}
      />

      {/* Mobile — logo + theme toggle */}
      <div className="contact-page__chrome contact-page__chrome--mobile-only lg:hidden">
        <button
          type="button"
          onClick={handleBackToHome}
          className="flex min-h-[44px] items-center hover:opacity-80 transition-opacity touch-manipulation"
        >
          <GGJLogoFull iconSize={32} fontSize={17} boostMark={false} />
        </button>
        {themeToggle}
      </div>

      {/* Desktop — theme toggle top-right */}
      <div className="contact-page__chrome hidden lg:flex">{themeToggle}</div>

      {/* Left — brand + illustration (desktop) */}
      <aside className="contact-page__brand-panel" aria-label={t('views.contact.supportReady')}>
        <button
          type="button"
          onClick={handleBackToHome}
          className="contact-page__brand-logo flex min-h-[44px] items-center hover:opacity-80 transition-opacity touch-manipulation"
        >
          <GGJLogoFull iconSize={36} fontSize={18} boostMark={false} />
        </button>

        <div className="contact-page__brand-inner">
          <div className="contact-page__brand-copy">
            <h1 className="contact-page__title">
              {t('views.contact.needAssistance')}
              <br />
              {t('views.contact.weAreHere')}
            </h1>
            <p className="contact-page__subtitle mt-2">
              {t('views.contact.supportReady')}
            </p>
          </div>

          <motion.div
            className="w-full"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <ContactIllustration
              isDark={isDark}
              alt={t('views.contact.illustrationAlt')}
            />
          </motion.div>
        </div>
      </aside>

      {/* Right — form */}
      <main className="contact-page__form-panel" key={i18n.language}>
        <div className="contact-page__mobile-hero">
          <ContactIllustration
            isDark={isDark}
            alt={t('views.contact.illustrationAlt')}
          />
          <h1 className="contact-page__title">
            {t('views.contact.needAssistance')} {t('views.contact.weAreHere')}
          </h1>
          <p className="contact-page__subtitle">{t('views.contact.supportReady')}</p>
        </div>

        <div className="contact-page__form-scroll">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-xl"
          >
            <div className="mb-6 lg:mb-8">
              <h2 className="contact-page__title text-2xl sm:text-3xl">
                {t('views.contact.havingQuestions')}
              </h2>
              <p className="contact-page__subtitle mt-2">
                {t('views.contact.dropMessage')}
              </p>
            </div>

            <ContactHeadquarters mapTitle={t('views.contact.mapTitle')} />

          {/* Success Message — full width */}
          {submitStatus === 'success' && (
            <motion.div
              className="w-full p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm mb-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <p>{t('views.contact.successMessage')}</p>
              </div>
            </motion.div>
          )}
          
          {/* Error Message — full width */}
          {(submitStatus === 'error' || submitStatus === 'validation-error') && (
            <motion.div
              className="w-full p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm mb-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <p>
                  {submitStatus === 'validation-error' 
                    ? t('views.contact.errorValidation') 
                    : t('views.contact.errorGeneric')}
                </p>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="contact-page__label">
                  {t('views.contact.firstName')} *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`contact-page__input ${validationErrors.firstName ? 'border-red-500' : ''}`}
                  placeholder={t('views.contact.placeholderFirst')}
                  required
                />
                {validationErrors.firstName && (
                  <p className="mt-1 text-sm text-red-400">{validationErrors.firstName}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="lastName" className="contact-page__label">
                  {t('views.contact.lastName')}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="contact-page__input"
                  placeholder={t('views.contact.placeholderLast')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="contact-page__label">
                {t('views.contact.email')} *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`contact-page__input ${validationErrors.email ? 'border-red-500' : ''}`}
                placeholder={t('views.contact.placeholderEmail')}
                required
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-400">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="message" className="contact-page__label">
                {t('views.contact.message')} *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className={`contact-page__input min-h-[120px] resize-none ${
                  validationErrors.message ? 'border-red-500' : ''
                }`}
                placeholder={t('views.contact.placeholderMessage')}
                required
              />
              {validationErrors.message && (
                <p className="mt-1 text-sm text-red-400">{validationErrors.message}</p>
              )}
            </div>

            <div>
              <label className="contact-page__label">
                {t('views.contact.attachmentsOptional')}
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  isDragging
                    ? 'border-[#5F8568] bg-[#5F8568]/10'
                    : 'border-[var(--contact-dropzone-border)] hover:border-[#5F8568]/50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                  ref={fileInputRef}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--contact-subtext)]" />
                  <p className="text-base text-[var(--contact-subtext)]">
                    {t('views.contact.dragDrop')}
                  </p>
                  <p className="text-sm mt-1 text-[var(--contact-input-placeholder)]">
                    {t('views.contact.maxFiles')}
                  </p>
                  {validationErrors.files && (
                    <p className="mt-1 text-xs text-red-400">{validationErrors.files}</p>
                  )}
                </label>
              </div>
              
              {/* Attached Files */}
              {attachedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 rounded-lg border border-[var(--contact-input-border)] bg-[var(--contact-input-bg)]">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <Paperclip className="w-4 h-4 flex-shrink-0 text-[var(--contact-subtext)]" />
                        <span className="text-sm truncate text-[var(--contact-input-text)]">{file.name}</span>
                        <span className="text-xs flex-shrink-0 text-[var(--contact-input-placeholder)]">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-400 hover:text-red-300 transition-colors ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="home-marketing-cta w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="xs" showText={false} />
                  {t('views.contact.sending')}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('views.contact.sendMessage')}
                </>
              )}
            </button>
          </form>

          <div className="contact-page__footer">
            <p className="text-xs">
              <button
                type="button"
                onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
              >
                {t('views.contact.privacyPolicy')}
              </button>
              {' • '}
              <button
                type="button"
                onClick={() => window.open('/terms-of-service', '_blank', 'noopener,noreferrer')}
              >
                {t('views.contact.termsOfService')}
              </button>
            </p>
            <p className="text-xs mt-1 text-[var(--contact-footer-text)]">
              {t('views.contact.allRightsReserved')}
            </p>
          </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default ContactUsPage;
